import React from 'react';

/** A stable, bounded window that still shows five pages at either end. */
export const documentPageWindow = (page: number, totalPages: number): number[] => {
  const count = Math.min(5, totalPages);
  const first = Math.max(1, Math.min(page - 2, totalPages - count + 1));
  return Array.from({ length: count }, (_, i) => first + i);
};

interface Props {
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  showPageSize?: boolean;
  navigationLabel?: string;
  compact?: boolean;
}

const DocumentPagination: React.FC<Props> = ({ page, pageSize, total, onPageChange, onPageSizeChange, showPageSize = true, navigationLabel = 'Dokumentseiten', compact = false }) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const current = Math.max(1, Math.min(page, totalPages));
  const button = 'inline-flex h-10 min-w-[40px] items-center justify-center rounded-lg border border-gray-200 bg-white px-2 text-sm font-semibold text-gray-600 hover:bg-gray-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700';
  return (
    <div className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-2 border-t border-gray-200 dark:border-gray-700 ${compact ? 'py-2' : 'pt-4'}`}>
      <div className="flex flex-wrap items-center gap-3 text-sm text-gray-500 dark:text-gray-400">
        <span aria-live="polite">{total ? `${(current - 1) * pageSize + 1}–${Math.min(current * pageSize, total)} von ${total} Dokumenten` : '0 Dokumente'}</span>
        {showPageSize && <label className="inline-flex items-center gap-2">
          Pro Seite
          <select aria-label="Dokumente pro Seite" value={pageSize} onChange={event => onPageSizeChange(Number(event.target.value))} className="h-10 rounded-lg border border-gray-200 bg-white px-2 text-gray-900 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-700 dark:bg-gray-800 dark:text-white">
            {[24, 48, 96].map(size => <option key={size} value={size}>{size}</option>)}
          </select>
        </label>}
      </div>
      <nav aria-label={navigationLabel} className="flex flex-wrap items-center gap-1">
        <button className={button} aria-label="Erste Seite" disabled={current === 1} onClick={() => onPageChange(1)}>«</button>
        <button className={button} aria-label="Vorherige Seite" disabled={current === 1} onClick={() => onPageChange(current - 1)}>‹</button>
        {documentPageWindow(current, totalPages).map(number => (
          <button key={number} aria-label={`Seite ${number}`} aria-current={current === number ? 'page' : undefined} className={current === number ? 'inline-flex h-10 min-w-[40px] items-center justify-center rounded-lg border border-primary-600 bg-primary-600 px-2 text-sm font-bold text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-400' : `${button} hidden sm:inline-flex`} onClick={() => onPageChange(number)}>{number}</button>
        ))}
        <button className={button} aria-label="Nächste Seite" disabled={current === totalPages} onClick={() => onPageChange(current + 1)}>›</button>
        <button className={button} aria-label="Letzte Seite" disabled={current === totalPages} onClick={() => onPageChange(totalPages)}>»</button>
        <span className="ml-1 text-xs text-gray-500 dark:text-gray-400">{current}/{totalPages}</span>
      </nav>
    </div>
  );
};

export default DocumentPagination;
